import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronDown, ChevronRight, ChevronUp, Clock3, Copy, Eye, FileText, GripVertical, ImageOff, MoreHorizontal, Pencil, Plus, Search, Trash2, UtensilsCrossed } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Site } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { ask } from '../components/Confirm';
import { cleanValues, EntityForm, type FieldSpec, useAssetUrls } from '../components/EntityForm';
import { Menu } from '../components/Menu';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Field, Loading, PageHeader, Toggle } from '../components/ui';
import { hoursOf, STATUS, type Status } from './FacilitiesPage';

/** Cuisine / type chip colours, picked by a stable hash so each cuisine keeps its colour. */
const CHIP: [string, string][] = [
  ['#6d28d9', '#f1ebff'],
  ['#047857', '#e7f8f1'],
  ['#be123c', '#ffe9ee'],
  ['#1d4ed8', '#e8f0ff'],
  ['#b45309', '#fff4e0'],
  ['#0f766e', '#e0f5f2'],
];
const chipOf = (s: string) => CHIP[[...s].reduce((n, c) => n + c.charCodeAt(0), 0) % CHIP.length]!;

/** All meal periods → status: open if any period is open now. */
function venueStatus(v: Venue, tz: string): Status {
  const st = v.mealPeriods.map((p) => hoursOf(p.hours, tz).status);
  if (st.includes('open')) return 'open';
  if (st.includes('closed')) return 'closed';
  if (st.includes('request')) return 'request';
  return 'unknown';
}
const menuCount = (v: Venue) => v.sections.reduce((n, s) => n + s.items.length, 0);

function StatCard({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: number; label: string; sub: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        <span className="stat-tile-sub">{sub}</span>
      </span>
    </div>
  );
}

export function RestaurantsPage() {
  const site = useSite();
  return <NeedsSite>{site ? <Restaurants key={site.id} site={site} /> : <PageHeader title="Restaurants & Bars" />}</NeedsSite>;
}

function Restaurants({ site }: { site: Site }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const url = useAssetUrls();
  const key = ['venues', site.id];
  const query = useQuery({ queryKey: key, queryFn: () => api.get<{ items: Venue[] }>(`/sites/${site.id}/venues`).then((r) => r.items) });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const [order, setOrder] = useState<Venue[] | null>(null);
  const items = order ?? query.data ?? [];
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [status, setStatus] = useState('all');
  const [drag, setDrag] = useState<number | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [edit, setEdit] = useState<Venue | 'new' | null>(null);
  const navigate = useNavigate();
  const openMenu = (v: Venue) => navigate(`/experience/restaurants/${v.id}/menu`);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const editable = can('ADMIN');

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put(`/sites/${site.id}/venues/order`, { ids }),
    onSettled: async () => {
      await invalidate();
      setOrder(null);
    },
  });
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: Partial<Venue> }) => api.patch(`/venues/${id}`, body), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/venues/${id}`), onSuccess: invalidate });
  const toForm = (v: Venue) => ({ ...v, imageUrl: url(v.imageAssetId) });
  const duplicate = useMutation({
    mutationFn: async (v: Venue) => {
      const copy = await api.post<{ id: string }>(`/sites/${site.id}/venues`, { ...cleanValues(toForm(v), venueFields), name: `${v.name} (copy)`.slice(0, 80) });
      if (v.sections.length)
        await api.put(`/venues/${copy.id}/menu`, {
          sections: v.sections.map((s) => ({
            name: s.name,
            items: s.items.map((i) => ({ name: i.name, description: i.description, price: i.price === null ? null : Number(i.price), imageAssetId: i.imageAssetId, allergens: i.allergens, available: i.available })),
          })),
        });
    },
    onSuccess: invalidate,
  });
  const moveTo = (from: number, to: number) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m!);
    reorder.mutate(next.map((x) => x.id));
    setOrder(next);
  };

  const statusOf = useMemo(() => new Map(items.map((v) => [v.id, venueStatus(v, site.timezone)])), [items, site.timezone, tick]);
  const cuisines = [...new Set(items.map((v) => v.cuisine).filter(Boolean) as string[])].sort();
  const filtering = q.trim() !== '' || cat !== 'all' || status !== 'all';
  const shown = items.filter((v) => {
    const s = q.trim().toLowerCase();
    if (s && ![v.name, v.venueType, v.cuisine].some((x) => x?.toLowerCase().includes(s))) return false;
    if (cat !== 'all' && (v.cuisine ?? '') !== cat) return false;
    if (status === 'hidden') return !v.enabled;
    if (status !== 'all' && statusOf.get(v.id) !== status) return false;
    return true;
  });
  const sel = items.find((v) => v.id === selId) ?? shown[0] ?? null;

  if (query.isLoading) return <Loading />;
  return (
    <>
      <PageHeader title="Restaurants & Bars" sub="Venues with photos, opening times and their menus." />
      <div className="stat-strip">
        <StatCard icon={<UtensilsCrossed size={22} />} tone="violet" value={items.length} label="Total venues" sub="Restaurants, bars and cafés" />
        <StatCard icon={<CheckCircle2 size={22} />} tone="green" value={items.filter((v) => v.enabled).length} label="Enabled venues" sub="Visible on TV" />
        <StatCard icon={<FileText size={22} />} tone="amber" value={items.reduce((n, v) => n + menuCount(v), 0)} label="Menu items" sub="Across all menus" />
        <StatCard icon={<Clock3 size={22} />} tone="blue" value={items.filter((v) => statusOf.get(v.id) === 'open').length} label="Open now" sub="At the hotel's time" />
      </div>

      <div className="fac-layout">
        <div className="card fill">
          <div className="pkg-head" style={{ flexWrap: 'wrap' }}>
            <span className="row" style={{ gap: 8, marginRight: 'auto' }}>
              <span className="pkg-head-title">Venues</span>
              <span className="pkg-count">{items.length}</span>
            </span>
            <label className="search" style={{ flex: '1 1 200px', maxWidth: 260 }}>
              <Search size={16} />
              <input placeholder="Search venues…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Select value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Cuisine">
              <option value="all">All cuisines</option>
              {cuisines.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              <option value="all">All statuses</option>
              <option value="open">Open now</option>
              <option value="closed">Closed now</option>
              <option value="hidden">Hidden on TV</option>
            </Select>
            {editable && (
              <button className="btn primary" onClick={() => setEdit('new')}>
                <Plus size={16} /> Add venue
              </button>
            )}
          </div>
          {shown.length === 0 ? (
            <Empty>{items.length ? 'No venue matches.' : 'No venues yet. Add the first restaurant or bar; it appears on the TVs after saving.'}</Empty>
          ) : (
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="fac-table">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    <th>Venue</th>
                    <th>Cuisine / type</th>
                    <th>Opening hours</th>
                    <th>Status</th>
                    <th>Enabled</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((v) => {
                    const i = items.indexOf(v);
                    const st = statusOf.get(v.id)!;
                    const img = url(v.imageAssetId);
                    const [fg, bg] = chipOf(v.cuisine ?? '');
                    return (
                      <tr
                        key={v.id}
                        className={`${sel?.id === v.id ? 'selected' : ''}${drag === i ? ' dragging' : ''}`}
                        style={!v.enabled ? { opacity: 0.55 } : undefined}
                        onClick={() => setSelId(v.id)}
                        draggable={editable && !filtering}
                        onDragStart={(e) => {
                          setDrag(i);
                          setOrder(items);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragOver={(e) => {
                          if (drag === null || drag === i) return;
                          e.preventDefault();
                          const next = [...items];
                          const [m] = next.splice(drag, 1);
                          next.splice(i, 0, m!);
                          setOrder(next);
                          setDrag(i);
                        }}
                        onDragEnd={() => {
                          setDrag(null);
                          if (order) reorder.mutate(order.map((x) => x.id));
                        }}
                      >
                        <td>
                          <span className="row" style={{ gap: 2, flexWrap: 'nowrap', color: 'var(--muted)' }} title={filtering ? 'Clear the filters to reorder' : 'Drag to reorder'}>
                            <GripVertical size={14} className="drag-handle" /> {i + 1}
                          </span>
                        </td>
                        <td>
                          <span className="row" style={{ gap: 12, flexWrap: 'nowrap' }}>
                            <span className="fac-thumb venue">{img ? <img src={img} alt="" /> : <ImageOff size={18} />}</span>
                            <span style={{ minWidth: 0 }}>
                              <strong style={{ display: 'block' }}>{v.name}</strong>
                              {v.venueType && <span className="muted fac-sub">{v.venueType}</span>}
                            </span>
                          </span>
                        </td>
                        <td>{v.cuisine ? <span className="chip" style={{ color: fg, background: bg, whiteSpace: 'nowrap' }}>{v.cuisine}</span> : <span className="muted">—</span>}</td>
                        <td>
                          <span className="row" style={{ gap: 8, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                            <Clock3 size={16} style={{ marginTop: 2, flex: 'none' }} />
                            <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                              {v.mealPeriods.length ? (
                                v.mealPeriods.map((p, k) => (
                                  <span key={k} style={{ display: 'block' }} title={p.label}>
                                    {p.hours}
                                  </span>
                                ))
                              ) : (
                                <span className="muted">Not set</span>
                              )}
                            </span>
                          </span>
                        </td>
                        <td>
                          <span className={`state-pill ${STATUS[st].cls}`} style={{ whiteSpace: 'nowrap' }}>
                            <span className="state-dot" /> {st === 'closed' ? 'Closed' : STATUS[st].label}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Toggle on={v.enabled} onChange={(on) => editable && patch.mutate({ id: v.id, body: { enabled: on } })} label="Shown on TV" />
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          {editable && (
                            <>
                              <button className="btn small" onClick={() => openMenu(v)} style={{ marginRight: 4 }}>
                                <UtensilsCrossed size={14} /> Menu
                              </button>
                              <button className="icon-btn" title="Edit" onClick={() => setEdit(v)}>
                                <Pencil size={16} />
                              </button>
                              <button className="icon-btn" title="Duplicate (with its menu)" onClick={() => duplicate.mutate(v)}>
                                <Copy size={16} />
                              </button>
                              <button className="icon-btn danger-icon" title="Delete" onClick={() => ask(`Delete ${v.name}? Its menu is deleted too and it disappears from the TVs.`, () => remove.mutate(v.id))}>
                                <Trash2 size={16} />
                              </button>
                              <Menu
                                minWidth={180}
                                button={({ onClick }) => (
                                  <button type="button" className="icon-btn" title="More" onClick={onClick}>
                                    <MoreHorizontal size={16} />
                                  </button>
                                )}
                              >
                                <button type="button" className="more-item" disabled={i === 0} onClick={() => moveTo(i, 0)}>
                                  <ChevronUp size={15} /> Move to top
                                </button>
                                <button type="button" className="more-item" disabled={i === items.length - 1} onClick={() => moveTo(i, items.length - 1)}>
                                  <ChevronDown size={15} /> Move to bottom
                                </button>
                              </Menu>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="muted" style={{ fontSize: 13, marginTop: 12 }}>
            Showing <strong>{shown.length}</strong> of {items.length} venues
          </div>
          <ErrorBox error={patch.error ?? remove.error ?? reorder.error ?? duplicate.error} />
        </div>

        {sel && (
          <VenueDetail
            v={sel}
            img={url(sel.imageAssetId)}
            status={statusOf.get(sel.id)!}
            onEdit={editable ? () => setEdit(sel) : undefined}
            onMenu={editable ? () => openMenu(sel) : undefined}
          />
        )}
      </div>

      {edit && (
        <EntityForm
          title={edit === 'new' ? 'Add venue' : `Edit ${edit.name}`}
          fields={venueFields}
          initial={edit === 'new' ? { enabled: true, currency: 'USD', mealPeriods: [], reservations: false } : toForm(edit)}
          wide
          onClose={() => setEdit(null)}
          onSave={async (values) => {
            const body = cleanValues(values, venueFields);
            if (edit === 'new') await api.post(`/sites/${site.id}/venues`, body);
            else await api.patch(`/venues/${edit.id}`, body);
            await invalidate();
          }}
        />
      )}
    </>
  );
}

function VenueDetail({ v, img, status, onEdit, onMenu }: { v: Venue; img: string | null; status: Status; onEdit?: () => void; onMenu?: () => void }) {
  const [fg, bg] = chipOf(v.cuisine ?? '');
  return (
    <div className="stack fac-detail">
      <div className="card">
        <div className="fac-hero">{img ? <img src={img} alt="" /> : <ImageOff size={30} />}</div>
        <div className="row" style={{ justifyContent: 'space-between', marginTop: 16, flexWrap: 'nowrap', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 22 }}>{v.name}</h2>
          <span className={`state-pill ${STATUS[status].cls}`} style={{ whiteSpace: 'nowrap' }}>
            <span className="state-dot" /> {status === 'closed' ? 'Closed' : STATUS[status].label}
          </span>
        </div>
        {v.venueType && <div style={{ fontWeight: 700, marginTop: 4 }}>{v.venueType}</div>}
        {v.description && <p className="muted fac-desc">{v.description}</p>}
        <div className="row" style={{ gap: 6, marginTop: 10 }}>
          {v.cuisine && (
            <span className="chip" style={{ color: fg, background: bg }}>
              {v.cuisine}
            </span>
          )}
          {v.reservations && <span className="chip">Reservations</span>}
        </div>
        <div className="venue-facts">
          <div className="fac-detail-row" style={{ alignItems: 'flex-start' }}>
            <Clock3 size={17} />
            <span>Opening hours</span>
            <span className="fac-detail-val">
              {v.mealPeriods.length
                ? v.mealPeriods.map((p, k) => (
                    <span key={k} style={{ display: 'block' }}>
                      {p.label ? <span className="muted" style={{ fontWeight: 500 }}>{p.label} </span> : null}
                      {p.hours}
                    </span>
                  ))
                : '—'}
            </span>
          </div>
          <div className="fac-detail-row">
            <UtensilsCrossed size={17} />
            <span>Menu items</span>
            <span className="fac-detail-val">{menuCount(v)} items</span>
          </div>
          <div className="fac-detail-row">
            <Eye size={17} />
            <span>Visible on TV</span>
            <span className="fac-detail-val" style={{ color: v.enabled ? '#047857' : undefined }}>
              {v.enabled ? 'Yes' : 'No (hidden)'}
            </span>
          </div>
        </div>
        {(onEdit || onMenu) && (
          <div className="row" style={{ gap: 10, marginTop: 14, flexWrap: 'nowrap' }}>
            {onEdit && (
              <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onEdit}>
                <Pencil size={16} /> Edit venue
              </button>
            )}
            {onMenu && (
              <button className="btn primary" style={{ flex: 1, justifyContent: 'center' }} onClick={onMenu}>
                <UtensilsCrossed size={16} /> Edit menu
              </button>
            )}
          </div>
        )}
      </div>
      <div className="card">
        <div className="row" style={{ gap: 8, fontWeight: 700 }}>
          <Eye size={18} color="var(--brand)" /> Guest view on TV
        </div>
        <div className="venue-tv" style={img ? { backgroundImage: `linear-gradient(90deg, rgba(10,8,30,.85) 35%, rgba(10,8,30,.1)), url(${img})` } : undefined}>
          <strong>{v.name}</strong>
          {v.description && <p>{v.description}</p>}
          <span className="venue-tv-btn">
            View Menu <ChevronRight size={12} />
          </span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Restaurants & bars ───────────────────────────

interface MenuItemRow {
  name: string;
  description: string | null;
  price: string | number | null;
  imageAssetId: string | null;
  allergens: string[];
  available: boolean;
}
export interface Venue {
  id: string;
  name: string;
  venueType: string | null;
  cuisine: string | null;
  description: string | null;
  imageAssetId: string | null;
  mealPeriods: { label: string; hours: string }[];
  location: string | null;
  phone: string | null;
  currency: string;
  reservations: boolean;
  enabled: boolean;
  sections: { id: string; name: string; items: MenuItemRow[] }[];
}

const MealPeriodsEditor = ({ value, onChange }: { value: { label: string; hours: string }[]; onChange: (v: { label: string; hours: string }[]) => void }) => (
  <Field label="Meal periods" hint="Shown under the restaurant description, e.g. Breakfast 07:00 – 10:30.">
    {value.map((p, i) => (
      <div key={i} className="row">
        <input value={p.label} placeholder="Breakfast" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
        <input value={p.hours} placeholder="07:00 – 10:30" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, hours: e.target.value } : x)))} />
        <button className="icon-btn" onClick={() => onChange(value.filter((_, j) => j !== i))}>
          <Trash2 size={14} />
        </button>
      </div>
    ))}
    <div>
      <button className="btn small" onClick={() => onChange([...value, { label: '', hours: '' }])}>
        <Plus size={13} /> Add period
      </button>
    </div>
  </Field>
);

export const venueFields: FieldSpec[] = [
  { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'The Horizon' },
  { key: 'venueType', label: 'Type', type: 'text', placeholder: 'Main Restaurant' },
  { key: 'cuisine', label: 'Cuisine', type: 'text', placeholder: 'International Cuisine' },
  { key: 'imageAssetId', label: 'Photo', type: 'image', urlKey: 'imageUrl' },
  { key: 'description', label: 'Description', type: 'textarea' },
  {
    key: 'mealPeriods',
    label: 'Meal periods',
    type: 'custom',
    render: (v, set) => <MealPeriodsEditor value={(v.mealPeriods as { label: string; hours: string }[]) ?? []} onChange={(mealPeriods) => set({ mealPeriods: mealPeriods.filter((p) => p.label || p.hours) })} />,
  },
  { key: 'location', label: 'Location', type: 'text' },
  { key: 'phone', label: 'Phone / extension', type: 'text' },
  { key: 'currency', label: 'Currency for menu prices', type: 'text', placeholder: 'USD, EUR, QAR…' },
  { key: 'reservations', label: 'Show "Reserve a Table"', type: 'toggle', hint: 'Guests are asked to call the venue for now.' },
  { key: 'enabled', label: 'Shown on TV', type: 'toggle' },
];
